import React, { useCallback, useContext } from 'react'
import { useTranslation } from 'react-i18next'

import {
  useFormDataV2,
  useFormValidation,
  validateEmailWithLocale,
  validateIsNotBlankWithLocale,
  validateNewPasswordWithLocale,
} from '@monorepo/utils'
import Input from '../Input'
import { ApiCore, Locale, useRegister } from '@monorepo/api'
import { ContextApp } from '@monorepo/service'
import { bonus } from '../../constants'

const VALIDATORS = {
  rules: {
    email: [validateIsNotBlankWithLocale, validateEmailWithLocale],
    password: [validateIsNotBlankWithLocale, validateNewPasswordWithLocale],
  },
  defaultData: {
    email: '',
    password: '',
  },
}

const FormEmail: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { t } = useTranslation()
  const { sid, setDataRegister } = useContext(ContextApp)

  const { handleRequest, commonErrorMsg, fieldErrorMsg, isLoading } = useRegister({
    sid,
    bonusId: bonus?.bonusId,
    setDataRegister,
  })

  const emailErrorMessage = fieldErrorMsg ? fieldErrorMsg['email'] : null
  const passwordErrorMessage = fieldErrorMsg ? fieldErrorMsg['password'] : null

  const { validateForm } = useFormValidation(VALIDATORS.rules)
  const { form, onValidate, onInputChange } = useFormDataV2({
    validateForm,
    defaultData: VALIDATORS.defaultData,
  })

  const handleSubmit = useCallback(() => {
    onValidate(
      'email',
      'password',
    )((data) => {
      handleRequest({
        account_registered_by: ApiCore.PublicAccountRegistrationTypeEnum.Email,
        language: Locale.get(),
        country_of_residence: 'KAZ',
        currency: 'KZT',
        privacy_policy: true,
        terms_and_conditions: true,
        receive_promos_enabled: true,
        ...data,
      })
    })
  }, [handleRequest, onValidate])

  return (
    <>
      <Input
        autoFocus
        placeholder={t('form.placeholders.email')}
        errorMsg={emailErrorMessage ?? form.errors.email}
        onChange={onInputChange('email')}
      />
      <Input
        type="password"
        autoComplete="new-password"
        placeholder={t('form.placeholders.password')}
        errorMsg={commonErrorMsg ?? passwordErrorMessage ?? form.errors.password}
        onChange={onInputChange('password')}
      />
      {React.isValidElement(children)
        ? React.cloneElement(children as React.ReactElement, {
            onClick: handleSubmit,
            isloading: isLoading ? 'loading' : '',
            disabled: form.statuses?.email !== 'valid' || form.statuses?.password !== 'valid',
          })
        : null}
    </>
  )
}

export default FormEmail
